import { create } from 'zustand';
import { PageMetadata } from '@/features/pages/types/page.types';
import {
  Collection,
  DisplayedCollectionSection,
  CollectionFilter,
} from '../types/collection.types';

export type CollectionMetadata = Omit<Collection, 'sections'>;

// Helper function to strip nested data
function stripToMetadata(collection: Collection): CollectionMetadata {
  const { id, title, isPublic, isFavourite, tags, updatedAt, createdAt, archivedAt } = collection;
  return { id, title, isPublic, isFavourite, tags, updatedAt, createdAt, archivedAt };
}

type CollectionState = {
  loading: boolean;
  collections: CollectionMetadata[];
  currentCollection: Collection | null;
  currentCollections: CollectionMetadata[];
  activeFilter: CollectionFilter;
  collectionStructure: DisplayedCollectionSection[];
  closedSections: Record<string, boolean>;
  getCurrentCollectionPageMetadata: () => PageMetadata[];

  // Simple actions (setters)
  setLoading: (loading: boolean) => void;
  setCollections: (collections: Collection[]) => void;
  setCurrentCollection: (collection: Collection | null) => void;
  setCurrentCollections: (collections: Collection[]) => void;
  setActiveFilter: (filter: CollectionFilter) => void;
  setCollectionStructure: (structure: DisplayedCollectionSection[]) => void;
  updateCollection: (id: string, update: Partial<Collection>) => void;
  toggleSection: (sectionId: string) => void;
  resetCurrentCollection: () => void;
};

export const useCollectionStore = create<CollectionState>((set, get) => ({
  // Initial state
  loading: false,
  collections: [],
  currentCollection: null,
  currentCollections: [],
  activeFilter: CollectionFilter.All,
  collectionStructure: [],
  closedSections: {},

  getCurrentCollectionPageMetadata: () => {
    return get()
      .collectionStructure.map(c => c.section.pages)
      .flat();
  },

  // Simple setters
  setLoading: loading => {
    set({ loading });
  },

  setCollections: collections => {
    set({ collections: collections.map(stripToMetadata) });
  },

  setCurrentCollection: collection => {
    set({ currentCollection: collection });
  },

  setCurrentCollections: collections => {
    set({ currentCollections: collections.map(stripToMetadata) });
  },

  setActiveFilter: filter => {
    set({ activeFilter: filter });
  },

  setCollectionStructure: structure => {
    set({ collectionStructure: structure });
  },

  updateCollection: (id, update) => {
    set(state => ({
      collections: state.collections.map(c => (c.id === id ? { ...c, ...update } : c)),
      currentCollection:
        state.currentCollection?.id === id
          ? { ...state.currentCollection, ...update }
          : state.currentCollection,
    }));
  },

  toggleSection: (sectionId: string) => {
    set(state => ({
      closedSections: {
        ...state.closedSections,
        [sectionId]: !state.closedSections[sectionId],
      },
    }));
  },

  resetCurrentCollection: () => {
    set({
      currentCollection: null,
      collectionStructure: [],
      closedSections: {},
    });
  },
}));
